import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { CircularProgress, IconButton, MenuItem, Select } from '@mui/material';
import {
  CalendarMonthOutlined,
  ChevronLeft,
  ChevronRight,
  EmailOutlined,
  ExpandMoreOutlined,
  GroupsOutlined,
  HowToRegOutlined,
  ShowChartOutlined,
  TerrainOutlined,
  TimerOutlined,
  StraightenOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

import './team-dashboard.page.css';
import { Team } from '../../../../models/training.models';
import { TeamInactiveAthlete, TeamMemberStats, TeamAttendanceMember } from '../../../../models/team.models';
import useService from '../../../../hooks/service.hook';
import useSnackbarHelper from '../../../../hooks/snackbar.hook';
import TeamStatsService from '../../../../services/team-stats.service';
import AnalyticsService from '../../../../services/analytics.service';
import {
  formatDuration,
  formatDurationHHmm,
  getElevationMeasurementUnit,
  getEquipmentPerformanceLabel,
  getMeasurementUnit,
  getRaceResultOrdinal,
} from '../../../../helpers/general.helpers';
import {
  computeCurrentSeasonStart,
  getDefaultAnnualAnchor,
  getIsoWeekRange,
  getSeasonStartForYear,
  getSeasonYearForSeasonStart,
  resolveSeasonRange,
  toApiDateRange,
} from '../../../../helpers/datetime.helpers';

dayjs.extend(isoWeek);

interface TeamDashboardPageProps {
  team: Team;
  /** Base path for in-dashboard links (default `/teams/:id`) */
  basePath?: string;
}

type LbWindow = 'weekly' | 'period' | 'seasonal';
type LbMetric = 'duration' | 'distance' | 'ascent';

const getInitials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const avatarColor = (name: string): string => {
  const colors = ['#2563EB', '#7C3AED', '#16A34A', '#D97706', '#DC2626', '#DB2777', '#0891B2', '#4F46E5'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const formatRelativeThreadDate = (dateStr: string): string => {
  const d = dayjs(dateStr);
  if (d.isSame(dayjs(), 'day')) return 'Today';
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Yesterday';
  return d.format('MMM D');
};

const getMessagePreview = (html: string): string => {
  const withLineBreaks = (html || '').replace(/<(p|div|br)[^>]*>/gi, '\n').replace(/<[^>]*>/g, '');
  const decoded = withLineBreaks
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  const firstLine = decoded
    .split('\n')
    .map((line) => line.trim())
    .find((line) => line.length > 0);
  return firstLine ? `${firstLine}...` : '';
};

const metricValue = (m: TeamMemberStats, metric: LbMetric): number => {
  if (metric === 'distance') return m.totalDistance ?? 0;
  if (metric === 'ascent') return m.totalElevationGain ?? 0;
  return m.totalDuration ?? 0;
};

const formatMetric = (value: number, metric: LbMetric): string => {
  if (metric === 'duration') return `${formatDurationHHmm(value)}hrs`;
  if (metric === 'distance')
    return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })} ${getMeasurementUnit()}`;
  return `${Math.round(value).toLocaleString()} ${getElevationMeasurementUnit()}`;
};

const pctColor = (pct: number, role: 'coach' | 'athlete'): string => {
  if (pct >= 85) return role === 'coach' ? '#2563EB' : '#16A34A';
  if (pct >= 50) return '#D97706';
  return '#DC2626';
};

const TeamDashboardPage: FC<TeamDashboardPageProps> = ({ team, basePath }) => {
  const navigate = useNavigate();
  const teamStatsService = useService(TeamStatsService);
  const analyticsService = useService(AnalyticsService);
  const { showSnackbar } = useSnackbarHelper();
  const attendanceRef = useRef<HTMLDivElement | null>(null);
  const isCoach = team.role?.toLowerCase() === 'coach';
  const teamBasePath = basePath || `/teams/${team.id}`;

  const [lbWindow, setLbWindow] = useState<LbWindow>('weekly');
  const [lbMetric, setLbMetric] = useState<LbMetric>('duration');
  const [lbBaseDate, setLbBaseDate] = useState<Dayjs>(dayjs());
  const [annualAnchor, setAnnualAnchor] = useState<Dayjs | null>(null);
  const [lbLoading, setLbLoading] = useState(true);
  const [lbCoaches, setLbCoaches] = useState<TeamMemberStats[]>([]);
  const [lbAthletes, setLbAthletes] = useState<TeamMemberStats[]>([]);
  const [inactiveAthletes, setInactiveAthletes] = useState<TeamInactiveAthlete[]>([]);
  const [lbCoachesExpanded, setLbCoachesExpanded] = useState(false);
  const [lbAthletesExpanded, setLbAthletesExpanded] = useState(false);
  const [lbCoachesCollapsed, setLbCoachesCollapsed] = useState(false);
  const [lbAthletesCollapsed, setLbAthletesCollapsed] = useState(false);
  const [attnCollapsed, setAttnCollapsed] = useState(false);

  const [attWeekStart, setAttWeekStart] = useState<Dayjs>(() => getIsoWeekRange(dayjs()).start);
  const [attLoading, setAttLoading] = useState(true);
  const [attCoaches, setAttCoaches] = useState<TeamAttendanceMember[]>([]);
  const [attAthletes, setAttAthletes] = useState<TeamAttendanceMember[]>([]);
  const [attCoachesCollapsed, setAttCoachesCollapsed] = useState(false);
  const [attAthletesCollapsed, setAttAthletesCollapsed] = useState(false);

  const [monthDate, setMonthDate] = useState<Dayjs>(() => dayjs().startOf('month'));
  const [monthLoading, setMonthLoading] = useState(true);
  const [monthAvg, setMonthAvg] = useState<{
    totalDuration: number;
    totalDistance: number;
    totalElevationGain: number;
    sessionPercent: number;
    practicePercent: number;
  } | null>(null);

  const [raceExpanded, setRaceExpanded] = useState(true);
  const [lbActionMemberId, setLbActionMemberId] = useState<string | null>(null);
  const [attnActionMemberId, setAttnActionMemberId] = useState<string | null>(null);
  const [attActionMemberId, setAttActionMemberId] = useState<string | null>(null);

  const openMemberSchedule = (memberId: string) => {
    navigate(`${teamBasePath}/athlete-schedules?member=${memberId}&sub=schedule`);
  };

  const openMemberTrainingLog = (memberId: string) => {
    navigate(`${teamBasePath}/athlete-schedules?member=${memberId}&sub=training-log`);
  };

  const stats = team.stats;
  const counts = team.memberCounts;
  const latestThread = team.quickInfo?.latestThread;
  const latestRace = team.quickInfo?.latestRace;

  useEffect(() => {
    if (!isCoach) return;
    analyticsService
      .getAnalyticsAnnualStartDate()
      .then((response) => {
        const annualStartDateStr = response.data.data?.annual_start_date;
        setAnnualAnchor(annualStartDateStr ? dayjs(annualStartDateStr) : getDefaultAnnualAnchor());
      })
      .catch(() => setAnnualAnchor(getDefaultAnnualAnchor()));
  }, [isCoach]);

  const getLbRange = (window: LbWindow, base: Dayjs, anchor: Dayjs | null) => {
    if (window === 'weekly') return getIsoWeekRange(base);
    if (window === 'period') {
      return { start: base.startOf('month'), end: base.endOf('month') };
    }
    if (anchor) {
      const baseSeasonYear = getSeasonYearForSeasonStart(anchor, base);
      const baseSeasonStart = getSeasonStartForYear(anchor, baseSeasonYear);
      return resolveSeasonRange(anchor, baseSeasonStart, baseSeasonYear);
    }
    return { start: base.startOf('year'), end: base.endOf('year') };
  };

  const lbRange = useMemo(() => getLbRange(lbWindow, lbBaseDate, annualAnchor), [lbWindow, lbBaseDate, annualAnchor]);

  useEffect(() => {
    if (!isCoach || !team.id) return;
    setLbLoading(true);
    const { start, end } = toApiDateRange(lbRange.start, lbRange.end);
    teamStatsService
      .getBasicTeamStats(String(team.id), start, end)
      .then((response: any) => {
        const athletes: TeamMemberStats[] = response.data.data.athletes ?? [];
        const coaches: TeamMemberStats[] = response.data.data.coaches ?? [];
        setLbCoaches([...coaches].sort((a, b) => metricValue(b, lbMetric) - metricValue(a, lbMetric)));
        setLbAthletes([...athletes].sort((a, b) => metricValue(b, lbMetric) - metricValue(a, lbMetric)));
        setInactiveAthletes(response.data.data.inactiveAthletes ?? []);
        setLbLoading(false);
      })
      .catch(() => {
        setLbLoading(false);
        showSnackbar('Unable to load training leaderboard.', 'error');
      });
  }, [isCoach, team.id, lbRange.start.valueOf(), lbRange.end.valueOf()]);

  // Re-sort when metric changes without refetch
  useEffect(() => {
    if (!isCoach) return;
    setLbCoaches((prev) => [...prev].sort((a, b) => metricValue(b, lbMetric) - metricValue(a, lbMetric)));
    setLbAthletes((prev) => [...prev].sort((a, b) => metricValue(b, lbMetric) - metricValue(a, lbMetric)));
  }, [isCoach, lbMetric]);

  useEffect(() => {
    if (!isCoach || !team.id) return;
    setAttLoading(true);
    const end = attWeekStart.add(6, 'day');
    const { start, end: endIso } = toApiDateRange(attWeekStart, end);
    teamStatsService
      .getAttendanceTeamStats(String(team.id), start, endIso)
      .then((response: any) => {
        const data = response.data.data;
        if (Array.isArray(data)) {
          setAttCoaches([]);
          setAttAthletes(data);
        } else {
          setAttCoaches(data?.coaches ?? []);
          setAttAthletes(data?.athletes ?? []);
        }
        setAttLoading(false);
      })
      .catch(() => {
        setAttLoading(false);
        showSnackbar('Unable to load team attendance.', 'error');
      });
  }, [isCoach, team.id, attWeekStart.valueOf()]);

  useEffect(() => {
    if (!isCoach || !team.id) return;
    setMonthLoading(true);
    const { start, end } = toApiDateRange(monthDate.startOf('month'), monthDate.endOf('month'));
    teamStatsService
      .getBasicTeamStats(String(team.id), start, end)
      .then((response: any) => {
        setMonthAvg(response.data.data.teamAverage ?? null);
        setMonthLoading(false);
      })
      .catch(() => {
        setMonthLoading(false);
        showSnackbar('Unable to load monthly averages.', 'error');
      });
  }, [isCoach, team.id, monthDate.valueOf()]);

  const navLb = (dir: number) => {
    if (lbWindow === 'weekly') setLbBaseDate((d) => d.add(dir, 'week'));
    else if (lbWindow === 'period') setLbBaseDate((d) => d.add(dir, 'month'));
    else setLbBaseDate((d) => d.add(dir, 'year'));
  };

  const lbWindowLabel = () => {
    if (lbWindow === 'weekly') {
      return `${lbRange.start.format('MMM D')} – ${lbRange.end.format('MMM D, YYYY')}`;
    }
    if (lbWindow === 'period') return lbRange.start.format('MMMM YYYY');
    return `${lbRange.start.format('MMM D, YYYY')} – ${lbRange.end.format('MMM D, YYYY')}`;
  };

  const maxMetric = Math.max(...[...lbCoaches, ...lbAthletes].map((m) => metricValue(m, lbMetric)), 1);

  const renderLbSection = (
    label: string,
    members: TeamMemberStats[],
    expanded: boolean,
    onToggleExpanded: () => void,
    collapsed: boolean,
    onToggleCollapsed: () => void
  ) => {
    const displayed = expanded ? members : members.slice(0, 5);
    return (
      <div className="td-lb-section">
        <div className="td-lb-section-header">
          <span className="td-lb-section-title">{label}</span>
          <span className="td-lb-section-count">{members.length}</span>
          <IconButton
            size="small"
            className="td-lb-collapse-btn"
            onClick={onToggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={collapsed ? `Expand ${label}` : `Collapse ${label}`}
          >
            <ExpandMoreOutlined className={`td-lb-collapse-icon${collapsed ? ' collapsed' : ''}`} />
          </IconButton>
        </div>
        {!collapsed ? (
          <>
            {members.length === 0 ? (
              <div className="td-empty">No training data for this range</div>
            ) : (
              <div className="td-lb-list">
                {displayed.map((m, i) => {
                  const val = metricValue(m, lbMetric);
                  const width = Math.max(4, Math.round((val / maxMetric) * 100));
                  const rankClass = i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : 'normal';
                  const memberId = String(m.id);
                  const actionsOpen = lbActionMemberId === memberId;
                  return (
                    <div key={memberId}>
                      <div
                        className="td-lb-row"
                        onClick={() => setLbActionMemberId((prev) => (prev === memberId ? null : memberId))}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') setLbActionMemberId((prev) => (prev === memberId ? null : memberId));
                        }}
                      >
                        <span className={`td-lb-rank ${rankClass}`}>{i + 1}</span>
                        <span className="td-avatar" style={{ background: avatarColor(m.name) }}>
                          {getInitials(m.name)}
                        </span>
                        <span className="td-lb-name">{m.name}</span>
                        <span className="td-lb-val">{formatMetric(val, lbMetric)}</span>
                        <div className="td-lb-bar-wrap">
                          <div className="td-lb-bar" style={{ width: `${width}%` }} />
                        </div>
                      </div>
                      {actionsOpen ? (
                        <div className="td-member-actions td-member-actions-lb">
                          <button
                            type="button"
                            className="td-member-action primary"
                            onClick={() => {
                              setLbActionMemberId(null);
                              openMemberSchedule(memberId);
                            }}
                          >
                            View Schedule
                          </button>
                          <button
                            type="button"
                            className="td-member-action secondary"
                            onClick={() => {
                              setLbActionMemberId(null);
                              openMemberTrainingLog(memberId);
                            }}
                          >
                            Training Log
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}

            {members.length > 5 ? (
              <button type="button" className="td-view-all" onClick={onToggleExpanded}>
                {expanded ? 'Show Less' : 'View All'}
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    );
  };

  const attDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => attWeekStart.add(i, 'day'));
  }, [attWeekStart]);

  const buildAttRow = (member: TeamAttendanceMember, role: 'coach' | 'athlete', rank: number) => {
    const byDate: Record<string, Array<{ attended?: boolean | null; time?: string; title?: string }>> = {};
    (member.trainings || []).forEach((t) => {
      if (!t.time) return;
      const key = dayjs(t.time).format('YYYY-MM-DD');
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push({
        attended: t.attended,
        time: dayjs(t.time).format('HH:mm'),
        title: t.title,
      });
    });

    const total = member.total_sessions || 0;
    const attended = member.total_attended || 0;
    const pct = total > 0 ? Math.round((attended / total) * 100) : 0;
    const memberId = String(member.member_id);
    const actionsOpen = attActionMemberId === memberId;
    const colSpan = attDays.length + 2;

    return (
      <React.Fragment key={memberId}>
        <tr
          className="td-att-row"
          onClick={() => setAttActionMemberId((prev) => (prev === memberId ? null : memberId))}
        >
          <td>
            <div className="td-att-name">
              <span className={`td-att-rank ${rank <= 3 ? `rank-${rank}` : ''}`}>{rank}</span>
              <span className="td-avatar" style={{ background: avatarColor(member.name) }}>
                {getInitials(member.name)}
              </span>
              {member.name}
            </div>
          </td>
          {attDays.map((day) => {
            const key = day.format('YYYY-MM-DD');
            const sessions = byDate[key] || [];
            const isToday = day.isSame(dayjs(), 'day');
            const isFuture = day.isAfter(dayjs(), 'day');
            return (
              <td key={key} className={isToday ? 'td-att-today-col' : undefined}>
                <div className="td-att-boxes">
                  {sessions.length === 0 ? (
                    <span className="td-att-box empty" />
                  ) : (
                    sessions.map((s, idx) => {
                      let cls = 'pending';
                      let label: React.ReactNode = '·';
                      if (isFuture || s.attended == null) {
                        cls = 'pending';
                        label = '·';
                      } else if (s.attended) {
                        cls = 'present';
                        label = '✓';
                      } else {
                        cls = 'absent';
                        label = '✗';
                      }
                      return (
                        <span key={idx} className={`td-att-box ${cls} ${role}`} title={s.title || ''}>
                          {label}
                        </span>
                      );
                    })
                  )}
                </div>
              </td>
            );
          })}
          <td className="td-att-total">
            <span className="td-att-total-num" style={{ color: pctColor(pct, role) }}>
              {attended}/{total}
            </span>
            <span className="td-att-pct" style={{ color: pctColor(pct, role) }}>
              {pct}%
            </span>
          </td>
        </tr>
        {actionsOpen ? (
          <tr className="td-att-actions-row">
            <td colSpan={colSpan}>
              <div className="td-member-actions td-member-actions-att">
                <button
                  type="button"
                  className="td-member-action primary"
                  onClick={() => {
                    setAttActionMemberId(null);
                    openMemberSchedule(memberId);
                  }}
                >
                  View Schedule
                </button>
                <button
                  type="button"
                  className="td-member-action secondary"
                  onClick={() => {
                    setAttActionMemberId(null);
                    openMemberTrainingLog(memberId);
                  }}
                >
                  Training Log
                </button>
              </div>
            </td>
          </tr>
        ) : null}
      </React.Fragment>
    );
  };

  const daySessionCounts = useMemo(() => {
    return attDays.map((day) => {
      const key = day.format('YYYY-MM-DD');
      const ids = new Set<string>();
      [...attCoaches, ...attAthletes].forEach((m) => {
        (m.trainings || []).forEach((t) => {
          if (t.time && dayjs(t.time).format('YYYY-MM-DD') === key) {
            ids.add(String(t.training_id ?? `${t.title}-${t.time}`));
          }
        });
      });
      return ids.size;
    });
  }, [attDays, attCoaches, attAthletes]);

  const ringMetrics = monthAvg
    ? [
        {
          key: 'attendance',
          label: 'Attendance',
          value: Math.round(monthAvg.practicePercent),
          unit: '%',
          color: '#2563EB',
          grad: ['#3B82F6', '#1D4ED8'],
          icon: <GroupsOutlined style={{ fontSize: 14, color: '#2563EB', opacity: 0.7 }} />,
          fill: Math.min(100, monthAvg.practicePercent) / 100,
        },
        {
          key: 'duration',
          label: 'Duration',
          value: Number((monthAvg.totalDuration / 3600).toFixed(1)),
          unit: 'h',
          color: '#7C3AED',
          grad: ['#8B5CF6', '#6D28D9'],
          icon: <TimerOutlined style={{ fontSize: 14, color: '#7C3AED', opacity: 0.7 }} />,
          fill: Math.min(1, monthAvg.totalDuration / (5 * 3600)),
        },
        {
          key: 'distance',
          label: 'Distance',
          value: Number(monthAvg.totalDistance.toFixed(1)),
          unit: getMeasurementUnit(),
          color: '#16A34A',
          grad: ['#22C55E', '#15803D'],
          icon: <StraightenOutlined style={{ fontSize: 14, color: '#16A34A', opacity: 0.7 }} />,
          fill: Math.min(1, monthAvg.totalDistance / 20),
        },
        {
          key: 'ascent',
          label: 'Ascent',
          value: Math.round(monthAvg.totalElevationGain),
          unit: getElevationMeasurementUnit(),
          color: '#D97706',
          grad: ['#F59E0B', '#B45309'],
          icon: <TerrainOutlined style={{ fontSize: 14, color: '#D97706', opacity: 0.7 }} />,
          fill: Math.min(1, monthAvg.totalElevationGain / 2000),
        },
      ]
    : [];

  const circumference = 2 * Math.PI * 42;

  return (
    <div className="team-dashboard-page">
      {/* Quick access */}
      <div className="td-qa-grid">
        <button type="button" className="td-qa-card" onClick={() => navigate(`${teamBasePath}/team-trainings`)}>
          <span className="td-qa-arrow">›</span>
          <div className="td-qa-icon" style={{ background: '#F3EDFF' }}>
            <CalendarMonthOutlined style={{ color: '#7C3AED' }} />
          </div>
          <div className="td-qa-label">Team Training Plan</div>
          <div className="td-qa-values">
            <span className="td-qa-value">{stats ? `${formatDurationHHmm(stats.plannedDuration)}h` : '—'}</span>
            <span className="td-qa-divider" />
            <span className="td-qa-value">
              {stats
                ? `${Number(stats.plannedDistance).toLocaleString(undefined, {
                    maximumFractionDigits: 1,
                  })} ${getMeasurementUnit()}`
                : '—'}
            </span>
          </div>
          <div className="td-qa-sub">Planned Duration • Distance this week</div>
        </button>

        <button type="button" className="td-qa-card" onClick={() => navigate(`${teamBasePath}/athlete-schedules`)}>
          <span className="td-qa-arrow">›</span>
          <div className="td-qa-icon" style={{ background: '#DCFCE7' }}>
            <ShowChartOutlined style={{ color: '#16A34A' }} />
          </div>
          <div className="td-qa-label">Athlete Schedules &amp; Training Logs</div>
          <div className="td-qa-values">
            <span className="td-qa-value">{stats ? `${formatDurationHHmm(stats.avgSessionDuration)}h` : '—'}</span>
            <span className="td-qa-divider" />
            <span className="td-qa-value">
              {stats
                ? `${Number(stats.avgSessionDistance).toLocaleString(undefined, {
                    maximumFractionDigits: 1,
                  })} ${getMeasurementUnit()}`
                : '—'}
            </span>
          </div>
          <div className="td-qa-sub">Avg Duration • Avg Distance</div>
        </button>

        <button type="button" className="td-qa-card" onClick={() => navigate(`${teamBasePath}/roster`)}>
          <span className="td-qa-arrow">›</span>
          <div className="td-qa-icon" style={{ background: '#EFF6FF' }}>
            <GroupsOutlined style={{ color: '#2563EB' }} />
          </div>
          <div className="td-qa-label">Team Roster</div>
          <div className="td-qa-value">{counts?.totalCount ?? '—'}</div>
          <div className="td-qa-sub">Members</div>
        </button>

        {isCoach ? (
          <button
            type="button"
            className="td-qa-card"
            onClick={() => attendanceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            <div className="td-qa-icon" style={{ background: '#FEF3C7' }}>
              <HowToRegOutlined style={{ color: '#D97706' }} />
            </div>
            <div className="td-qa-label">Team Attendance</div>
            <div className="td-qa-value">{stats ? `${Math.round(stats.practicePercent)}%` : '—'}</div>
            <div className="td-qa-sub">Weekly Average</div>
          </button>
        ) : null}
      </div>

      {/* Latest message */}
      <button
        type="button"
        className="td-qa-card td-latest-msg"
        disabled={!latestThread}
        onClick={() => {
          if (!latestThread) return;
          navigate(`${teamBasePath}/messages?message=${latestThread.id}`);
        }}
      >
        <span className="td-qa-arrow">›</span>
        <div className="td-latest-msg-inner">
          <div className="td-qa-icon" style={{ background: '#DBEAFE', marginBottom: 0 }}>
            <EmailOutlined style={{ color: '#2563EB' }} />
          </div>
          <div className="td-latest-msg-body">
            <div className="td-latest-msg-top">
              <span className="td-qa-label">Latest Message</span>
              {latestThread && !latestThread.read ? <span className="td-badge-new">NEW</span> : null}
              <span className="td-latest-msg-time">
                {latestThread ? formatRelativeThreadDate(latestThread.createdAt) : ''}
              </span>
            </div>
            {latestThread ? (
              <>
                <div className="td-latest-msg-title">{latestThread.title}</div>
                <div className="td-latest-msg-preview">
                  <span className="td-avatar sm" style={{ background: avatarColor(latestThread.creatorName) }}>
                    {getInitials(latestThread.creatorName)}
                  </span>
                  <span>
                    {latestThread.creatorName} — {getMessagePreview(latestThread.message)}
                  </span>
                </div>
              </>
            ) : (
              <div className="td-empty">No messages yet</div>
            )}
          </div>
        </div>
      </button>

      {/* Leaderboard / Attendance / Monthly avg — coaches only */}
      {isCoach ? (
        <>
          {/* Leaderboard */}
          <div className="td-card td-card-blue">
            <div className="td-card-title-row">
              <span className="td-card-title">Training Leaderboard</span>
              <div className="td-lb-controls">
                {annualAnchor ? (
                  <span className="td-lb-season">
                    Season starts week of {computeCurrentSeasonStart(annualAnchor).format('MMM D')}
                  </span>
                ) : null}
                <Select
                  size="small"
                  value={lbMetric}
                  onChange={(e) => setLbMetric(e.target.value as LbMetric)}
                  className="td-sel"
                >
                  <MenuItem value="duration">Duration</MenuItem>
                  <MenuItem value="distance">Distance</MenuItem>
                  <MenuItem value="ascent">Ascent</MenuItem>
                </Select>
              </div>
            </div>

            <div className="td-lb-toolbar">
              <div className="td-lb-toggle">
                {(['weekly', 'period', 'seasonal'] as LbWindow[]).map((w) => (
                  <button
                    key={w}
                    type="button"
                    className={lbWindow === w ? 'active' : ''}
                    onClick={() => {
                      setLbWindow(w);
                      setLbBaseDate(dayjs());
                    }}
                  >
                    {w === 'weekly' ? 'Weekly' : w === 'period' ? 'Period' : 'Seasonal'}
                  </button>
                ))}
              </div>
              <div className="td-nav">
                <IconButton size="small" onClick={() => navLb(-1)} aria-label="Previous">
                  <ChevronLeft />
                </IconButton>
                <span>{lbWindowLabel()}</span>
                <IconButton size="small" onClick={() => navLb(1)} aria-label="Next">
                  <ChevronRight />
                </IconButton>
              </div>
            </div>

            {lbLoading ? (
              <div className="td-loading">
                <CircularProgress size={32} />
              </div>
            ) : (
              <>
                {renderLbSection(
                  'Coaches',
                  lbCoaches,
                  lbCoachesExpanded,
                  () => setLbCoachesExpanded((v) => !v),
                  lbCoachesCollapsed,
                  () => setLbCoachesCollapsed((v) => !v)
                )}
                {renderLbSection(
                  'Athletes',
                  lbAthletes,
                  lbAthletesExpanded,
                  () => setLbAthletesExpanded((v) => !v),
                  lbAthletesCollapsed,
                  () => setLbAthletesCollapsed((v) => !v)
                )}
              </>
            )}

            {inactiveAthletes.length > 0 ? (
              <div className="td-attn">
                <div className="td-attn-title-row">
                  <span className="td-attn-title">Needs Attention</span>
                  <IconButton
                    size="small"
                    className="td-lb-collapse-btn"
                    onClick={() => setAttnCollapsed((v) => !v)}
                    aria-expanded={!attnCollapsed}
                    aria-label={attnCollapsed ? 'Expand Needs Attention' : 'Collapse Needs Attention'}
                  >
                    <ExpandMoreOutlined className={`td-lb-collapse-icon${attnCollapsed ? ' collapsed' : ''}`} />
                  </IconButton>
                </div>
                {!attnCollapsed &&
                  inactiveAthletes.map((a) => {
                    const memberId = String(a.id);
                    const actionsOpen = attnActionMemberId === memberId;
                    return (
                      <div key={memberId}>
                        <div
                          className="td-attn-item"
                          onClick={() => setAttnActionMemberId((prev) => (prev === memberId ? null : memberId))}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter')
                              setAttnActionMemberId((prev) => (prev === memberId ? null : memberId));
                          }}
                        >
                          <span className="td-attn-dot" />
                          <span className="td-avatar sm" style={{ background: avatarColor(a.name) }}>
                            {getInitials(a.name)}
                          </span>
                          <span className="td-attn-name">{a.name}</span>
                          <span className="td-attn-days">
                            {a.daysSinceLastTraining == null
                              ? 'No training logged'
                              : `${a.daysSinceLastTraining} days no training`}
                          </span>
                        </div>
                        {actionsOpen ? (
                          <div className="td-member-actions td-member-actions-attn">
                            <button
                              type="button"
                              className="td-member-action primary"
                              onClick={() => {
                                setAttnActionMemberId(null);
                                openMemberSchedule(memberId);
                              }}
                            >
                              View Schedule
                            </button>
                            <button
                              type="button"
                              className="td-member-action secondary"
                              onClick={() => {
                                setAttnActionMemberId(null);
                                openMemberTrainingLog(memberId);
                              }}
                            >
                              Training Log
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
              </div>
            ) : null}
          </div>

          {/* Attendance */}
          <div className="td-card td-card-blue" ref={attendanceRef}>
            <div className="td-card-title-row">
              <span className="td-card-title">Team Attendance</span>
              <div className="td-nav">
                <IconButton
                  size="small"
                  onClick={() => setAttWeekStart((d) => d.subtract(1, 'week'))}
                  aria-label="Previous week"
                >
                  <ChevronLeft />
                </IconButton>
                <span>
                  {attWeekStart.format('MMM D')} – {attWeekStart.add(6, 'day').format('MMM D, YYYY')}
                </span>
                <IconButton
                  size="small"
                  onClick={() => setAttWeekStart((d) => d.add(1, 'week'))}
                  aria-label="Next week"
                >
                  <ChevronRight />
                </IconButton>
              </div>
            </div>

            {attLoading ? (
              <div className="td-loading">
                <CircularProgress size={32} />
              </div>
            ) : (
              <>
                {[
                  {
                    title: 'Coaches',
                    role: 'coach' as const,
                    rows: attCoaches,
                    collapsed: attCoachesCollapsed,
                    onToggleCollapsed: () => setAttCoachesCollapsed((v) => !v),
                  },
                  {
                    title: 'Athletes',
                    role: 'athlete' as const,
                    rows: attAthletes,
                    collapsed: attAthletesCollapsed,
                    onToggleCollapsed: () => setAttAthletesCollapsed((v) => !v),
                  },
                ].map((section) => (
                  <div key={section.title} className={`td-att-section ${section.role}`}>
                    <div className="td-att-section-header">
                      <span className="td-att-section-title">{section.title}</span>
                      <span className="td-lb-section-count">{section.rows.length}</span>
                      <IconButton
                        size="small"
                        className="td-lb-collapse-btn"
                        onClick={section.onToggleCollapsed}
                        aria-expanded={!section.collapsed}
                        aria-label={section.collapsed ? `Expand ${section.title}` : `Collapse ${section.title}`}
                      >
                        <ExpandMoreOutlined className={`td-lb-collapse-icon${section.collapsed ? ' collapsed' : ''}`} />
                      </IconButton>
                    </div>
                    {!section.collapsed ? (
                      section.rows.length === 0 ? (
                        <div className="td-empty">No {section.title.toLowerCase()} for this week</div>
                      ) : (
                        <table className="td-att-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              {attDays.map((day, i) => {
                                const isToday = day.isSame(dayjs(), 'day');
                                return (
                                  <th key={day.format('YYYY-MM-DD')} className={isToday ? 'td-att-today' : undefined}>
                                    {day.format('ddd')}
                                    <br />
                                    <span className="td-att-day-count">({daySessionCounts[i]})</span>
                                  </th>
                                );
                              })}
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...section.rows]
                              .sort((a, b) => {
                                const pa = a.total_sessions ? a.total_attended / a.total_sessions : 0;
                                const pb = b.total_sessions ? b.total_attended / b.total_sessions : 0;
                                return pb - pa;
                              })
                              .map((m, idx) => buildAttRow(m, section.role, idx + 1))}
                          </tbody>
                        </table>
                      )
                    ) : null}
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Monthly avg rings */}
          <div className="td-card td-card-blue td-rings-card">
            <div className="td-rings-header">
              <div>
                <div className="td-card-title">Team Monthly Avg</div>
                <div className="td-card-sub">Avg per athlete across all sessions</div>
              </div>
              <div className="td-nav td-nav-pill">
                <IconButton
                  size="small"
                  onClick={() => setMonthDate((d) => d.subtract(1, 'month'))}
                  aria-label="Previous month"
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <span>{monthDate.format('MMMM YYYY')}</span>
                <IconButton size="small" onClick={() => setMonthDate((d) => d.add(1, 'month'))} aria-label="Next month">
                  <ChevronRight fontSize="small" />
                </IconButton>
              </div>
            </div>

            {monthLoading ? (
              <div className="td-loading">
                <CircularProgress size={32} />
              </div>
            ) : !monthAvg ? (
              <div className="td-empty" style={{ padding: 24 }}>
                No monthly data
              </div>
            ) : (
              <>
                <div className="td-ring-summary">
                  {ringMetrics.map((m, i) => (
                    <div key={m.key} className={`td-ring-summary-item ${i < ringMetrics.length - 1 ? 'bordered' : ''}`}>
                      <span className="td-ring-dot" style={{ background: m.color }} />
                      <div>
                        <div className="td-ring-summary-label">{m.label}</div>
                        <div className="td-ring-summary-val">
                          <strong>{m.value}</strong>
                          <span>{m.unit}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="td-ring-grid">
                  {ringMetrics.map((m) => {
                    const dash = circumference * m.fill;
                    const gap = circumference - dash;
                    return (
                      <div key={m.key} className="td-ring-card">
                        <div className="td-ring-card-label">
                          {m.icon}
                          <span>{m.label}</span>
                        </div>
                        <svg width="108" height="108" viewBox="0 0 108 108" className="td-ring-svg">
                          <defs>
                            <linearGradient id={`grad-${m.key}`} x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor={m.grad[0]} />
                              <stop offset="100%" stopColor={m.grad[1]} />
                            </linearGradient>
                          </defs>
                          <circle
                            cx="54"
                            cy="54"
                            r="42"
                            fill="none"
                            stroke={m.color}
                            strokeOpacity="0.08"
                            strokeWidth="8"
                          />
                          <circle
                            cx="54"
                            cy="54"
                            r="42"
                            fill="none"
                            stroke={`url(#grad-${m.key})`}
                            strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray={`${dash} ${gap}`}
                            transform="rotate(-90 54 54)"
                          />
                          <text x="54" y="55" textAnchor="middle" dominantBaseline="central">
                            <tspan fontSize="21" fontWeight="700" fill="#1C1C1E">
                              {m.value}
                            </tspan>
                            <tspan fontSize="10" fontWeight="500" fill="#8E8E93" dx="1">
                              {m.unit}
                            </tspan>
                          </text>
                        </svg>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </>
      ) : null}

      {/* Most recent race */}
      <div className="td-card td-card-blue td-race-card">
        <div className="td-rings-header">
          <div>
            <div className="td-card-title">Most Recent Race</div>
            <div className="td-card-sub">Latest race result and athlete performances</div>
          </div>
          <button type="button" className="td-view-races" onClick={() => navigate(`${teamBasePath}/race-log`)}>
            View All Races
          </button>
        </div>

        {!latestRace ? (
          <div className="td-empty" style={{ padding: '0 24px 24px' }}>
            No races yet
          </div>
        ) : (
          <div className="td-race-wrap">
            <div className="td-race-inner">
              <button type="button" className="td-race-head" onClick={() => setRaceExpanded((v) => !v)}>
                <div className="td-race-bar" />
                <div className="td-race-meta">
                  <div className="td-race-title-row">
                    <span className="td-race-name">{latestRace.name}</span>
                  </div>
                  <div className="td-race-submeta">
                    {dayjs(latestRace.date).format('MM/DD/YYYY')}
                    <span>·</span>
                    {dayjs(latestRace.date).format('h:mm A')}
                    {latestRace.location ? (
                      <>
                        <span>·</span>
                        {latestRace.location}
                      </>
                    ) : null}
                    <span>·</span>
                    {latestRace.raceLogs?.length ?? 0} athletes
                  </div>
                </div>
                <div className="td-race-tags">
                  {latestRace.sport ? <span className="td-tag purple">{latestRace.sport}</span> : null}
                  {latestRace.sportType ? <span className="td-tag red">{latestRace.sportType}</span> : null}
                </div>
                <div className="td-race-dur">
                  <div className="td-race-dur-val">{formatDuration(latestRace.duration)}</div>
                  <div className="td-race-dur-lbl">Duration</div>
                </div>
                <span className={`td-race-chev ${raceExpanded ? 'open' : ''}`}>▾</span>
              </button>
              {raceExpanded ? (
                <div className="td-race-body">
                  {(latestRace.raceLogs || []).length === 0 ? (
                    <div className="td-empty">No athlete results</div>
                  ) : (
                    <table className="td-race-table">
                      <thead>
                        <tr>
                          <th>Athlete</th>
                          <th>Result</th>
                          <th>Race type</th>
                          <th>Equipment</th>
                          <th>Equip. perf.</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {latestRace.raceLogs.map((log) => (
                          <tr key={log.id}>
                            <td>
                              <div className="td-att-name">
                                <span className="td-avatar" style={{ background: avatarColor(log.athleteName) }}>
                                  {getInitials(log.athleteName)}
                                </span>
                                {log.athleteName}
                              </div>
                            </td>
                            <td>{getRaceResultOrdinal(log.result)}</td>
                            <td className="muted">{log.raceType || 'N/A'}</td>
                            <td className="muted">{log.equipmentName || 'N/A'}</td>
                            <td className="muted">{getEquipmentPerformanceLabel(log.equipmentPerformance)}</td>
                            <td className="muted">N/A</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamDashboardPage;
